import apiClient from './client';

export enum NotificationType {
  SYSTEM = 'system',
  PAYMENT = 'payment',
  SUBSCRIPTION = 'subscription',
  PROMO = 'promo',
  REMINDER = 'reminder',
  ACHIEVEMENT = 'achievement',
}

export enum NotificationStatus {
  PENDING = 'pending',
  SCHEDULED = 'scheduled',
  SENT = 'sent',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

export enum AudienceType {
  ALL = 'all',
  PREMIUM = 'premium',
  FREE = 'free',
  BY_PREFERENCES = 'by_preferences',
  BY_ACTIVITY = 'by_activity',
}

export interface ActivityFilters {
  days_inactive?: number;
  days_active?: number;
}

export interface AudienceFilters {
  audience_type: AudienceType;
  activity_filters?: ActivityFilters;
  notification_types?: string[];
}

export interface PreviewNotificationDto {
  title: string;
  body: string;
  type: NotificationType;
  data?: Record<string, any>;
  audience_filters?: AudienceFilters;
}

export interface PreviewNotificationResponse {
  /** Сколько человек увидят сообщение в приложении. */
  estimated_recipients: number;
  /** Скольким из них дополнительно уйдёт push. */
  estimated_push_recipients: number;
  /** Сколько устройств получат push. */
  estimated_devices: number;
  breakdown: {
    total_users: number;
    premium_users: number;
    free_users: number;
    users_with_preferences: number;
    active_users: number;
    inactive_users: number;
  };
  message: string;
}

export interface CreateNotificationDto {
  title: string;
  body: string;
  type: NotificationType;
  data?: Record<string, any>;
  audience_filters?: AudienceFilters;
  scheduled_at?: string;
  priority?: number;
  is_sponsored?: boolean;
  is_premium?: boolean;
}

export interface CreateNotificationResponse {
  id: string;
  status: NotificationStatus;
  estimated_recipients: number | null;
  scheduled_at: string | null;
  message: string;
}

export interface NotificationListItem {
  id: string;
  title: string;
  body: string;
  type: NotificationType;
  status: NotificationStatus;
  created_at: string;
  scheduled_at: string | null;
  sent_at: string | null;
  created_by: string | null;
  estimated_recipients: number | null;
  /** Скольким адресовано фактически. */
  recipients_count: number;
  /** Скольким из них ушёл push. */
  push_sent_count: number;
  /** Открыли push (тап по уведомлению). */
  push_opened_count: number;
  /** Прочитали — пушем или открыв карточку в приложении. */
  read_count: number;
  push_open_rate: number;
  read_rate: number;
  /** Создано до перехода на модель получателей (ADR 0015). */
  is_legacy: boolean;
  is_sponsored: boolean;
  is_premium: boolean;
  /** Скрыто из приложения; статистика сохранена. */
  is_archived: boolean;
  archived_at: string | null;
  /** Метка триггерной кампании; null у ручных рассылок. */
  campaign: string | null;
  /** Период кампании, например `2026-08`. */
  campaign_period: string | null;
}

/** Какие уведомления показывать по признаку архивности. */
export type ArchivedFilter = 'all' | 'active' | 'archived';

/** Ручные рассылки или автоматические (триггерные кампании). */
export type SourceFilter = 'all' | 'manual' | 'auto';

export interface ListNotificationsParams {
  page?: number;
  limit?: number;
  status?: NotificationStatus;
  type?: NotificationType;
  search?: string;
  archived?: ArchivedFilter;
  source?: SourceFilter;
}

export interface ListNotificationsResponse {
  notifications: NotificationListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export interface NotificationStats {
  notification_id: string;
  title: string;
  type: string;
  status: string;
  created_at: string;
  scheduled_at: string | null;
  sent_at: string | null;
  is_legacy: boolean;
  estimated_recipients: number | null;
  /** Скольким адресовано. */
  recipients_count: number;
  /** Скольким ушёл push. */
  push_sent_count: number;
  /** Попыток принято Expo. */
  accepted_count: number;
  /** Подтверждено квитанцией Expo. */
  delivered_count: number;
  failed_count: number;
  /** Открыли push. */
  push_opened_count: number;
  /** Прочитали всего. */
  read_count: number;
  delivery_rate: number;
  push_open_rate: number;
  read_rate: number;
  status_breakdown: {
    success: number;
    failed: number;
    invalid_token: number;
    device_not_registered: number;
  };
}

export interface NotificationAnalyticsResponse {
  notification: NotificationStats;
  logs_summary: {
    total_logs: number;
    by_status: Record<string, number>;
    by_date: Array<{
      date: string;
      sent: number;
      delivered: number;
      opened: number;
    }>;
  };
}

export interface NotificationAnalyticsParams {
  start_date?: string;
  end_date?: string;
  status?: NotificationStatus;
}

export const notificationsApi = {
  /**
   * Preview notification (dry run)
   */
  async preview(dto: PreviewNotificationDto): Promise<PreviewNotificationResponse> {
    const response = await apiClient.post<PreviewNotificationResponse>(
      '/admin/notifications/preview',
      dto
    );
    return response.data;
  },

  /**
   * Create notification
   */
  async create(dto: CreateNotificationDto): Promise<CreateNotificationResponse> {
    const response = await apiClient.post<CreateNotificationResponse>(
      '/admin/notifications',
      dto
    );
    return response.data;
  },

  /**
   * List notifications
   */
  async list(params?: ListNotificationsParams): Promise<ListNotificationsResponse> {
    const response = await apiClient.get<ListNotificationsResponse>(
      '/admin/notifications',
      { params }
    );
    return response.data;
  },

  /**
   * Get notification analytics
   */
  async getAnalytics(
    id: string,
    params?: NotificationAnalyticsParams
  ): Promise<NotificationAnalyticsResponse> {
    const response = await apiClient.get<NotificationAnalyticsResponse>(
      `/admin/notifications/${id}/analytics`,
      { params }
    );
    return response.data;
  },

  /**
   * Cancel scheduled notification
   */
  async cancel(id: string): Promise<void> {
    await apiClient.patch(`/admin/notifications/${id}/cancel`);
  },

  /**
   * Archive notification — скрыть из приложения, сохранив статистику
   */
  async archive(id: string): Promise<void> {
    await apiClient.patch(`/admin/notifications/${id}/archive`);
  },

  /**
   * Unarchive notification — вернуть в приложение
   */
  async unarchive(id: string): Promise<void> {
    await apiClient.patch(`/admin/notifications/${id}/unarchive`);
  },

  /**
   * Delete notification — безвозвратно, вместе со статистикой
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/admin/notifications/${id}`);
  },
};

